import { FC, Fragment } from 'react';
import { EdnaWrapper } from '@providers/EdnaProvider';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import styled from 'styled-components';
import { PopoverProps } from './types';

const POPOVER_COLOR = 'white';

const Content = styled(PopoverPrimitive.Content)<{
  $width: string;
  $padding: string;
}>`
  pointer-events: auto;
  background-color: ${POPOVER_COLOR};
  padding: ${(props) => props.$padding};
  border-radius: 4px;
  box-sizing: border-box;
  width: ${(props) => props.$width};
  z-index: 999999;
  border: 1px solid ${(props) => props.theme.colors.grey100};
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.1),
    0px 0px 1px rgba(0, 0, 0, 0.05);
`;

/**
 * Use this component to wrap an element that will act as the "trigger".
 * When **clicking** the trigger, the popover will appear. For best accessibility,
 * the trigger should be a button.
 * You can position the popover with the `side` and `align` props. If you need to fine-tune
 * the position, you can use the `alignOffset` and `sideOffset` props as well.
 *
 * The popover has "collision avoidance" enabled. This means that if the popover
 * is triggered somewhere on the screen where it would collide with another
 * element or with the boundaries of the page (be hidden) then it will automatically
 * reposition itself so that it remains visible.
 *
 * Use this component for displaying rich content that otherwise would not work
 * with a tooltip. Content is dislpayed in a portal to avoid overlapping issues.
 */

const Popover: FC<PopoverProps> = ({
  align = 'start',
  alignOffset,
  children,
  className,
  content,
  disabled = false,
  open,
  onOpenChange,
  padding = '0',
  side = 'bottom',
  sideOffset,
  width = '250px',
  modal = false,
  portal = true,
}) => {
  const ContentWrapper = portal ? PopoverPrimitive.Portal : Fragment;

  return disabled ? (
    <>{children}</> // don't render the popover at all when disabled
  ) : (
    <EdnaWrapper>
      <PopoverPrimitive.Root
        open={open}
        onOpenChange={onOpenChange}
        modal={modal}
      >
        <PopoverPrimitive.Trigger asChild>{children}</PopoverPrimitive.Trigger>
        <ContentWrapper>
          <Content
            $width={width}
            $padding={padding}
            align={align}
            side={side}
            collisionPadding={4}
            className={className}
            alignOffset={alignOffset}
            sideOffset={sideOffset}
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            {content}
          </Content>
        </ContentWrapper>
      </PopoverPrimitive.Root>
    </EdnaWrapper>
  );
};

export default Popover;
